import React, { useContext, useMemo, useState, useCallback } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AlertTitle from "@mui/material/AlertTitle";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import SearchIcon from "@mui/icons-material/Search";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import { useMutation, useQuery } from "@apollo/client/react";
import { mutations, queries } from "@/graphql";
import { useTextDomainContext } from "@/app/providers";
import { usePageTitle } from "@/hooks";
import { enqueueSnackbar } from "notistack";
import moment from "moment";

interface UserItem {
  id: string;
  name?: string;
  email?: string;
  roles?: string[];
  companyId?: string;
  status?: string;
  createdAt?: string;
  lastLogin?: string;
  [key: string]: unknown;
}

interface TenantItem {
  id?: string;
  Id?: string;
  _id?: string;
  name?: string;
  createdBy?: string;
  users?: Array<{ userId?: string; user_id?: string; role?: string }>;
  [key: string]: unknown;
}

const normalizeRole = (role: string): string => {
  return String(role || "")
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
};

export const OrphanedUsers = (): React.ReactElement => {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);
  usePageTitle(gettext("Orphaned Users"));

  // Query all users
  const {
    data: usersData,
    loading: loadingUsers,
    refetch: refetchUsers,
  } = useQuery<{ allUsers: UserItem[] }>(queries.getUsersList, {});

  // Query all tenants
  const {
    data: tenantsData,
    loading: loadingTenants,
    refetch: refetchTenants,
  } = useQuery<{ tenantsList: TenantItem[] }>(queries.tenantsList, {});

  // Mutation to update user status
  const [updateUserMutation] = useMutation(mutations.updateUserByAdmin);

  // States
  const [updating, setUpdating] = useState(false);
  const [updateProgress, setUpdateProgress] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [filterSearch, setFilterSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<string>("company-owner");
  const [viewTab, setViewTab] = useState<number>(0); // 0 = Pending Deletion, 1 = All Orphaned, 2 = All Selected Role
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [singleDeletingId, setSingleDeletingId] = useState<string | null>(null);

  // Set of all tenant IDs
  const tenantIds = useMemo<Set<string>>(() => {
    const ids = new Set<string>();
    const tenants = tenantsData?.tenantsList || [];
    tenants.forEach((tenant) => {
      const id = tenant.id || tenant.Id || tenant._id;
      if (id) ids.add(String(id).trim());
    });
    return ids;
  }, [tenantsData]);

  // Set of all user IDs linked to at least one tenant
  const tenantUserIds = useMemo<Set<string>>(() => {
    const ids = new Set<string>();
    const tenants = tenantsData?.tenantsList || [];
    tenants.forEach((tenant) => {
      if (tenant?.createdBy) {
        ids.add(String(tenant.createdBy).trim());
      }
      tenant?.users?.forEach((u) => {
        if (u?.userId) ids.add(String(u.userId).trim());
        if (u?.user_id) ids.add(String(u.user_id).trim());
      });
    });
    return ids;
  }, [tenantsData]);

  // Check if a user belongs to any corporate group
  const userHasTenant = useCallback(
    (user: UserItem): boolean => {
      const userId = String(user.id).trim();
      if (tenantUserIds.has(userId)) return true;
      if (user.companyId && tenantIds.has(String(user.companyId).trim())) {
        return true;
      }
      return false;
    },
    [tenantUserIds, tenantIds],
  );

  // Distinct roles found in data for the filter dropdown
  const availableRoles = useMemo<string[]>(() => {
    const set = new Set<string>();
    const users = usersData?.allUsers || [];
    users.forEach((u) => {
      if (Array.isArray(u.roles)) {
        u.roles.forEach((r) => r && set.add(r));
      } else if (u.roles) {
        set.add(String(u.roles));
      }
    });
    return Array.from(set).sort();
  }, [usersData]);

  // Check if a user matches the selected role filter
  const matchesRole = useCallback(
    (user: UserItem, selectedRole: string): boolean => {
      const userRoles = Array.isArray(user.roles)
        ? user.roles
        : user.roles
          ? [String(user.roles)]
          : [];

      if (selectedRole === "all") return true;

      const targetNorm = normalizeRole(selectedRole);

      return userRoles.some((r) => {
        const norm = normalizeRole(r);
        return norm === targetNorm || norm.includes(targetNorm);
      });
    },
    [],
  );

  // Filtered users by role
  const roleMatchedUsers = useMemo<UserItem[]>(() => {
    const users = usersData?.allUsers || [];
    return users.filter((u) => matchesRole(u, roleFilter));
  }, [usersData, matchesRole, roleFilter]);

  // Users in this role who have 0 tenants
  const orphanedUsers = useMemo<UserItem[]>(() => {
    return roleMatchedUsers.filter((u) => !userHasTenant(u));
  }, [roleMatchedUsers, userHasTenant]);

  // Orphaned users who are NOT yet 'Deleted'
  const pendingOrphanedUsers = useMemo<UserItem[]>(() => {
    return orphanedUsers.filter(
      (u) =>
        String(u.status || "")
          .trim()
          .toLowerCase() !== "deleted",
    );
  }, [orphanedUsers]);

  // Active list based on active tab
  const tabUsers = useMemo<UserItem[]>(() => {
    switch (viewTab) {
      case 0:
        return pendingOrphanedUsers;
      case 1:
        return orphanedUsers;
      case 2:
      default:
        return roleMatchedUsers;
    }
  }, [viewTab, pendingOrphanedUsers, orphanedUsers, roleMatchedUsers]);

  // Displayed users with search filter applied
  const displayedUsers = useMemo<UserItem[]>(() => {
    if (!filterSearch.trim()) return tabUsers;
    const q = filterSearch.toLowerCase();
    return tabUsers.filter((user) => {
      const name = (user.name || "").toLowerCase();
      const email = (user.email || "").toLowerCase();
      const status = (user.status || "").toLowerCase();
      return name.includes(q) || email.includes(q) || status.includes(q);
    });
  }, [tabUsers, filterSearch]);

  // Refresh handler
  const handleRefresh = async () => {
    try {
      await Promise.all([refetchUsers(), refetchTenants()]);
      enqueueSnackbar(gettext("Data refreshed successfully"), {
        variant: "info",
      });
    } catch {
      enqueueSnackbar(gettext("Failed to refresh data"), {
        variant: "error",
      });
    }
  };

  // Bulk update handler: Set status to Deleted
  const handleExecuteBulkDelete = async () => {
    setConfirmOpen(false);
    if (pendingOrphanedUsers.length === 0) return;

    setUpdating(true);
    setUpdateProgress(0);

    let successCount = 0;
    let failCount = 0;
    const total = pendingOrphanedUsers.length;

    for (let i = 0; i < total; i++) {
      const targetUser = pendingOrphanedUsers[i];
      try {
        const response: any = await updateUserMutation({
          variables: {
            userId: targetUser.id,
            email: targetUser.email,
            status: "Deleted",
          },
        });

        if (
          response?.data?.updateUserAdmin?.success ||
          response?.data?.comUpdateUserAdmin?.success
        ) {
          successCount++;
        } else {
          failCount++;
        }
      } catch {
        failCount++;
      }
      setUpdateProgress(Math.round(((i + 1) / total) * 100));
    }

    setUpdating(false);

    // Refresh queries
    await Promise.all([refetchUsers(), refetchTenants()]);

    if (failCount === 0) {
      const msg = gettext(
        "Successfully updated %s user(s) to Deleted status",
      ).replace("%s", String(successCount));
      enqueueSnackbar(msg, { variant: "success" });
    } else {
      const msg = gettext("Completed with %s succeeded and %s failed")
        .replace("%s", String(successCount))
        .replace("%s", String(failCount));
      enqueueSnackbar(msg, { variant: "warning" });
    }
  };

  // Single user update handler: Set status to Deleted
  const handleSingleDelete = async (userItem: UserItem) => {
    setSingleDeletingId(userItem.id);
    try {
      const response: any = await updateUserMutation({
        variables: {
          userId: userItem.id,
          email: userItem.email,
          status: "Deleted",
        },
      });

      if (
        response?.data?.updateUserAdmin?.success ||
        response?.data?.comUpdateUserAdmin?.success
      ) {
        const displayName = userItem.email || userItem.name || "";
        const msg = gettext("User %s marked as Deleted").replace(
          "%s",
          displayName,
        );
        enqueueSnackbar(msg, { variant: "success" });
        await Promise.all([refetchUsers(), refetchTenants()]);
      } else {
        enqueueSnackbar(gettext("Failed to update user status"), {
          variant: "error",
        });
      }
    } catch {
      enqueueSnackbar(gettext("Error occurred while updating user"), {
        variant: "error",
      });
    } finally {
      setSingleDeletingId(null);
    }
  };

  const getStatusChipColor = (
    status?: string,
  ): "success" | "warning" | "error" | "default" => {
    switch (String(status || "").toLowerCase()) {
      case "active":
        return "success";
      case "inactive":
        return "warning";
      case "deleted":
      case "blocked":
        return "error";
      default:
        return "default";
    }
  };

  const isLoading = loadingUsers || loadingTenants;

  return (
    <Box sx={{ p: 3, maxWidth: 1400, margin: "0 auto" }}>
      {/* Header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        sx={{
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {gettext("Orphaned Users")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {gettext(
              "Manage and clean up users without associated corporate groups.",
            )}
          </Typography>
        </Box>

        <Button
          variant="outlined"
          startIcon={
            isLoading ? <CircularProgress size={16} /> : <RefreshIcon />
          }
          onClick={handleRefresh}
          disabled={isLoading || updating}
        >
          {gettext("Refresh Data")}
        </Button>
      </Stack>

      <Grid container spacing={3}>
        {/* Utility 1: Orphaned Company Owners */}
        <Grid size={{ xs: 12 }}>
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardHeader
              avatar={<DeleteSweepIcon color="primary" sx={{ fontSize: 32 }} />}
              title={
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {gettext("Cleanup Orphaned Users Without Corporate Groups")}
                </Typography>
              }
              subheader={gettext(
                "Finds users with the role 'company-owner' (or custom role) who have no corporate groups (tenants) and sets their status to 'Deleted'.",
              )}
            />
            <Divider />

            <CardContent>
              {/* Role Selector and Info */}
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                sx={{
                  justifyContent: "space-between",
                  alignItems: { xs: "stretch", sm: "center" },
                  mb: 3,
                }}
              >
                <FormControl size="small" sx={{ minWidth: 260 }}>
                  <InputLabel id="role-select-label">
                    {gettext("Target Role Filter")}
                  </InputLabel>
                  <Select
                    labelId="role-select-label"
                    value={roleFilter}
                    label={gettext("Target Role Filter")}
                    onChange={(e) => {
                      setRoleFilter(e.target.value);
                      setPage(0);
                    }}
                  >
                    <MenuItem value="company-owner">
                      <strong>{gettext("Company Owner (Standard)")}</strong>
                    </MenuItem>
                    <MenuItem value="all">
                      <em>{gettext("All Users (Any Role)")}</em>
                    </MenuItem>
                    <Divider />
                    {availableRoles
                      .filter((r) => normalizeRole(r) !== "companyowner")
                      .map((role) => (
                        <MenuItem key={role} value={role}>
                          {role}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>

                <Typography variant="caption" color="text.secondary">
                  {gettext("Total users in DB: %s | Total corporate groups: %s")
                    .replace("%s", String(usersData?.allUsers?.length || 0))
                    .replace(
                      "%s",
                      String(tenantsData?.tenantsList?.length || 0),
                    )}
                </Typography>
              </Stack>

              {/* Statistics Row */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      textAlign: "center",
                      backgroundColor: (theme) => theme.palette.grey[50],
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {gettext("Users with Target Role")}
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      {isLoading ? (
                        <CircularProgress size={20} />
                      ) : (
                        roleMatchedUsers.length
                      )}
                    </Typography>
                  </Paper>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      textAlign: "center",
                      backgroundColor: (theme) => theme.palette.grey[50],
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {gettext("With Corporate Group")}
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{ fontWeight: 700, color: "success.main" }}
                    >
                      {isLoading ? (
                        <CircularProgress size={20} />
                      ) : (
                        roleMatchedUsers.length - orphanedUsers.length
                      )}
                    </Typography>
                  </Paper>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      textAlign: "center",
                      backgroundColor: (theme) => theme.palette.grey[50],
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {gettext("Total Orphaned (0 Tenants)")}
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      {isLoading ? (
                        <CircularProgress size={20} />
                      ) : (
                        orphanedUsers.length
                      )}
                    </Typography>
                  </Paper>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      textAlign: "center",
                      backgroundColor:
                        pendingOrphanedUsers.length > 0
                          ? "warning.lighter"
                          : "success.lighter",
                      borderColor:
                        pendingOrphanedUsers.length > 0
                          ? "warning.light"
                          : "success.light",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {gettext("Pending Deletion (Not Deleted)")}
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 700,
                        color:
                          pendingOrphanedUsers.length > 0
                            ? "warning.main"
                            : "success.main",
                      }}
                    >
                      {isLoading ? (
                        <CircularProgress size={20} />
                      ) : (
                        pendingOrphanedUsers.length
                      )}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* Status Notice & Action Button */}
              {pendingOrphanedUsers.length > 0 ? (
                <Alert
                  severity="warning"
                  icon={<WarningAmberIcon />}
                  sx={{ mb: 3 }}
                  action={
                    <Button
                      variant="contained"
                      color="error"
                      size="medium"
                      startIcon={<PersonRemoveIcon />}
                      disabled={updating || isLoading}
                      onClick={() => setConfirmOpen(true)}
                    >
                      {gettext("Set Status to Deleted (%s)").replace(
                        "%s",
                        String(pendingOrphanedUsers.length),
                      )}
                    </Button>
                  }
                >
                  <AlertTitle sx={{ fontWeight: 600 }}>
                    {gettext("Action Required")}
                  </AlertTitle>
                  {gettext(
                    "Found %s user(s) matching '%role%' without any corporate group whose status is not yet 'Deleted'. Click the button on the right to set their status to 'Deleted'.",
                  )
                    .replace("%s", String(pendingOrphanedUsers.length))
                    .replace("%role%", roleFilter)}
                </Alert>
              ) : (
                <Alert
                  severity="success"
                  icon={<CheckCircleIcon />}
                  sx={{ mb: 3 }}
                >
                  <AlertTitle sx={{ fontWeight: 600 }}>
                    {gettext("All Clear")}
                  </AlertTitle>
                  {gettext(
                    "No active or inactive users without corporate groups found for the selected role. All matching orphaned users are already marked as Deleted.",
                  )}
                </Alert>
              )}

              {/* Progress bar during bulk update */}
              {updating && (
                <Box sx={{ mb: 3 }}>
                  <Stack
                    direction="row"
                    sx={{ justifyContent: "space-between", mb: 1 }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {gettext("Updating user statuses to 'Deleted'...")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {updateProgress}%
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={updateProgress}
                    color="error"
                  />
                </Box>
              )}

              {/* Tabs for filtering views */}
              <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
                <Tabs
                  value={viewTab}
                  onChange={(_, newTab) => {
                    setViewTab(newTab);
                    setPage(0);
                  }}
                >
                  <Tab
                    label={`${gettext("Pending Deletion")} (${pendingOrphanedUsers.length})`}
                  />
                  <Tab
                    label={`${gettext("All Orphaned")} (${orphanedUsers.length})`}
                  />
                  <Tab
                    label={`${gettext("All in Role")} (${roleMatchedUsers.length})`}
                  />
                </Tabs>
              </Box>

              {/* Search filter for table */}
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                sx={{
                  justifyContent: "space-between",
                  alignItems: { xs: "stretch", sm: "center" },
                  mb: 2,
                }}
              >
                <Typography variant="subtitle2" color="text.secondary">
                  {gettext("Showing %s user(s)").replace(
                    "%s",
                    String(displayedUsers.length),
                  )}
                </Typography>

                <TextField
                  size="small"
                  placeholder={gettext("Search by name, email, or status...")}
                  value={filterSearch}
                  onChange={(e) => {
                    setFilterSearch(e.target.value);
                    setPage(0);
                  }}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    },
                  }}
                  sx={{ width: { xs: "100%", sm: 300 } }}
                />
              </Stack>

              {/* Table of matching users */}
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow
                      sx={{
                        backgroundColor: (theme) => theme.palette.grey[100],
                      }}
                    >
                      <TableCell sx={{ fontWeight: 600 }}>
                        {gettext("Name")}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {gettext("Email")}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {gettext("Roles")}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {gettext("Corporate Group?")}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {gettext("Current Status")}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {gettext("Created Date")}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {gettext("Action")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {isLoading ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                          <CircularProgress size={30} />
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 1 }}
                          >
                            {gettext("Loading users and corporate groups...")}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : displayedUsers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            {filterSearch
                              ? gettext("No users matching your search.")
                              : gettext("No users found in this view.")}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      displayedUsers
                        .slice(
                          page * rowsPerPage,
                          page * rowsPerPage + rowsPerPage,
                        )
                        .map((row) => {
                          const hasTenant = userHasTenant(row);
                          const isAlreadyDeleted =
                            String(row.status || "")
                              .trim()
                              .toLowerCase() === "deleted";
                          const isRowUpdating = singleDeletingId === row.id;

                          return (
                            <TableRow key={row.id} hover>
                              <TableCell>{row.name || "—"}</TableCell>
                              <TableCell>{row.email || "—"}</TableCell>
                              <TableCell>
                                <Stack
                                  direction="row"
                                  spacing={0.5}
                                  sx={{ flexWrap: "wrap" }}
                                >
                                  {Array.isArray(row.roles) ? (
                                    row.roles.map((role) => (
                                      <Chip
                                        key={role}
                                        label={role}
                                        size="small"
                                        variant="outlined"
                                      />
                                    ))
                                  ) : (
                                    <Chip
                                      label={String(row.roles || "—")}
                                      size="small"
                                      variant="outlined"
                                    />
                                  )}
                                </Stack>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={
                                    hasTenant
                                      ? gettext("Linked")
                                      : gettext("None (0)")
                                  }
                                  size="small"
                                  color={hasTenant ? "success" : "default"}
                                  variant={hasTenant ? "filled" : "outlined"}
                                />
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={row.status || "Unknown"}
                                  size="small"
                                  color={getStatusChipColor(row.status)}
                                />
                              </TableCell>
                              <TableCell>
                                {row.createdAt
                                  ? moment(row.createdAt).format(
                                      "YYYY-MM-DD HH:mm",
                                    )
                                  : "—"}
                              </TableCell>
                              <TableCell align="right">
                                {isAlreadyDeleted ? (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {gettext("Already Deleted")}
                                  </Typography>
                                ) : (
                                  <Tooltip
                                    title={gettext("Set status to Deleted")}
                                  >
                                    <span>
                                      <Button
                                        size="small"
                                        color="error"
                                        variant="outlined"
                                        disabled={isRowUpdating || updating}
                                        onClick={() => handleSingleDelete(row)}
                                        startIcon={
                                          isRowUpdating ? (
                                            <CircularProgress size={12} />
                                          ) : (
                                            <PersonRemoveIcon fontSize="small" />
                                          )
                                        }
                                      >
                                        {gettext("Mark Deleted")}
                                      </Button>
                                    </span>
                                  </Tooltip>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })
                    )}
                  </TableBody>
                </Table>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  component="div"
                  count={displayedUsers.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(_, newPage) => setPage(newPage)}
                  onRowsPerPageChange={(event) => {
                    setRowsPerPage(parseInt(event.target.value, 10));
                    setPage(0);
                  }}
                />
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Confirmation Dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>
          {gettext("Confirm Deletion of Orphaned Users")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {gettext(
              "Are you sure you want to set the status to 'Deleted' for %s user(s) matching '%role%' who have no corporate groups?",
            )
              .replace("%s", String(pendingOrphanedUsers.length))
              .replace("%role%", roleFilter)}
          </DialogContentText>
          <DialogContentText
            sx={{ mt: 2, color: "text.secondary", fontSize: "0.875rem" }}
          >
            {gettext(
              "This will update their status column to 'Deleted' in the database. Deleted users will be hidden from default user lists and prevented from logging in.",
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={updating}>
            {gettext("Cancel")}
          </Button>
          <Button
            onClick={handleExecuteBulkDelete}
            color="error"
            variant="contained"
            disabled={updating}
            autoFocus
          >
            {gettext("Yes, Set Status to Deleted")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
